import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { Link, Persona, PersonaSize, Stack } from "@fluentui/react";
import React from "react";
import { ViewportType } from "../../../../shared/constants";
import { useViewport } from "../../../../styles/media";
import { TruncatedText } from "../../../TruncatedText/TruncatedText";
import { isSmallToLargeViewport } from "../../LeftNav";
import { personaLinkStyles, personaStyles, personaWrapperStyles, profileContainerStyles, profileInfoStyles } from "./LeftNavProfile.styles";
import { LeftNavPrimaryInfoProps, LeftNavProfileInternalProps, LeftNavProfilePersonaProps, LeftNavSecondaryInfoProps } from "./LeftNavProfile.types";

const LeftNavPersona: React.FC<{readonly size: number, readonly viewport: ViewportType, readonly theme: Theme, readonly personaInfo: LeftNavProfilePersonaProps,
    readonly primaryText: string, readonly disableInteraction: boolean}> = ({size, viewport, theme, personaInfo, primaryText, disableInteraction}) => {
    return (
        <Persona
            size={size}
            styles={personaStyles(viewport, theme, disableInteraction)}
            imageUrl={personaInfo.imageUrl}
            text={personaInfo.fallback === "initials" ? primaryText : undefined}
            hidePersonaDetails={true}
        />
    );
};

const LeftNavPrimaryInfo: React.FC<LeftNavPrimaryInfoProps> = (primaryInfo) => {
    return primaryInfo.kind === "heading"
    ? <TruncatedText stopPropagation={false} type="heading" tag={primaryInfo.headingTag ?? "h1"} variant="headerText">{primaryInfo.text}</TruncatedText>
    : <TruncatedText stopPropagation={false} variant="headerText">{primaryInfo.text}</TruncatedText>;
};

const LeftNavSecondaryInfo: React.FC<LeftNavSecondaryInfoProps> = ({text}) => {
    return <TruncatedText stopPropagation={false} variant="metadataText">{text}</TruncatedText>;
};

export const LeftNavProfile: React.FC<LeftNavProfileInternalProps> = ({
    isNavCollapsed,
    persona,
    primaryInfo,
    secondaryInfo
}) => {
    const theme = useTheme();
    const viewport = useViewport();

    let personaSize = PersonaSize.size48;
    if (isSmallToLargeViewport(viewport)) {
        personaSize = PersonaSize.size28;
    }

    return (
        <Stack horizontal={true} styles={profileContainerStyles(viewport)}>
            <Stack styles={personaWrapperStyles(isNavCollapsed)}>
                {persona.itemInteractiveProps ? (
                    <Link
                        styles={personaLinkStyles}
                        aria-label={persona.itemInteractiveProps["aria-label"]}
                        data-bi-id={persona.itemInteractiveProps["data-bi-id"]}
                        onClick={persona.itemInteractiveProps.itemInteraction.interactionType === "withOnClick" ? persona.itemInteractiveProps.itemInteraction.onClick : undefined}
                        href={persona.itemInteractiveProps.itemInteraction.interactionType === "withHref" ? persona.itemInteractiveProps.itemInteraction.href : undefined}
                    >
                        <LeftNavPersona size={personaSize} viewport={viewport} theme={theme} personaInfo={persona} primaryText={primaryInfo.text} disableInteraction={false} />
                    </Link>
                ) : (
                    <Stack styles={personaLinkStyles}>
                        <LeftNavPersona size={personaSize} viewport={viewport} theme={theme} personaInfo={persona} primaryText={primaryInfo.text} disableInteraction={true} />
                    </Stack>
                )}
            </Stack>
            {
                isNavCollapsed ? undefined : (
                    <Stack verticalAlign="center">
                        {primaryInfo.itemInteractiveProps ? (
                            <Link
                                styles={profileInfoStyles(theme, true)}
                                aria-label={primaryInfo.itemInteractiveProps["aria-label"]}
                                data-bi-id={primaryInfo.itemInteractiveProps["data-bi-id"]}
                                onClick={primaryInfo.itemInteractiveProps.itemInteraction.interactionType === "withOnClick" ? primaryInfo.itemInteractiveProps.itemInteraction.onClick : undefined}
                                href={primaryInfo.itemInteractiveProps.itemInteraction.interactionType === "withHref" ? primaryInfo.itemInteractiveProps.itemInteraction.href : undefined}
                            >
                                <LeftNavPrimaryInfo {...primaryInfo} />
                            </Link>
                        ) : (
                        <Stack styles={profileInfoStyles(theme, true, true)}>
                            <LeftNavPrimaryInfo {...primaryInfo} />
                        </Stack>
                    )}
                        {
                            isSmallToLargeViewport(viewport) ? undefined :
                                secondaryInfo.itemInteractiveProps ? (
                                    <Link
                                        styles={profileInfoStyles(theme, false)}
                                        aria-label={secondaryInfo.itemInteractiveProps["aria-label"]}
                                        data-bi-id={secondaryInfo.itemInteractiveProps["data-bi-id"]}
                                        onClick={secondaryInfo.itemInteractiveProps.itemInteraction.interactionType === "withOnClick" ? secondaryInfo.itemInteractiveProps.itemInteraction.onClick : undefined}
                                        href={secondaryInfo.itemInteractiveProps.itemInteraction.interactionType === "withHref" ? secondaryInfo.itemInteractiveProps.itemInteraction.href : undefined}
                                    >
                                        <LeftNavSecondaryInfo {...secondaryInfo} />
                                    </Link>
                                ) : (
                                    <Stack styles={profileInfoStyles(theme, false, true)}>
                                        <LeftNavSecondaryInfo {...secondaryInfo} />
                                    </Stack>
                                )
                        }
                    </Stack>
                )
            }
        </Stack>
    );
};
